import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiDelete, FiClock, FiCheck, FiLogOut, FiPause, FiCoffee, FiHeart, FiUser, FiMoreHorizontal, FiArrowLeft } from 'react-icons/fi'
import axios from 'axios'
import DesactivarQuioscoModal from '../components/DesactivarQuioscoModal'

/*
 * Quiosco — pantalla fullscreen con estética V2 (font-display, ui-card,
 * paleta cálida). Sin sidebar, sin sesión persistente: cada PIN dispara una
 * acción contra /api/quiosco/* y vuelve al teclado.
 */

const FEEDBACK_MS = 2500
const LONG_PRESS_MS = 5000

const ICON_MAP = {
  FiCoffee, FiPause, FiHeart, FiUser, FiMoreHorizontal, FiClock
}

function PinDots({ pin, min = 5, max = 8 }) {
  // Mostrar solo los huecos en uso: al menos `min` (longitud mínima de PIN);
  // crecer si el usuario teclea más, hasta `max`. Sin huecos "fantasma".
  const total = Math.min(max, Math.max(min, pin.length))
  return (
    <div className="flex justify-center gap-2.5">
      {Array.from({ length: total }).map((_, i) => {
        const filled = pin.length > i
        return (
          <div
            key={i}
            className={`w-10 h-12 sm:w-11 sm:h-14 rounded-[10px] flex items-center justify-center text-[22px] transition-all
              ${filled
                ? 'bg-[var(--ink)] text-[var(--bg)] border border-[var(--ink)]'
                : 'bg-white text-[var(--ink-4)] border border-[var(--line-2)]'
              }`}
          >
            {filled ? '●' : '·'}
          </div>
        )
      })}
    </div>
  )
}

function Reloj() {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  const hora = now.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' })
  const fecha = now.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', weekday: 'long', day: 'numeric', month: 'long' })
  return (
    <div className="text-right">
      <div className="font-display text-[42px] sm:text-[52px] leading-none text-[var(--ink)] tnum">{hora}</div>
      <div className="eyebrow mt-1.5 capitalize">{fecha}</div>
    </div>
  )
}

// Botón grande del teclado numérico — estilo ui-card, hover sutil, sin colores
function KeyBtn({ children, onClick, disabled, variant = 'default', ariaLabel }) {
  const base = 'h-[72px] rounded-[14px] font-display text-[26px] flex items-center justify-center transition-all duration-150 active:scale-[.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-1)] focus-visible:ring-offset-2 disabled:opacity-40 disabled:pointer-events-none'
  const styles = {
    default: 'bg-white text-[var(--ink)] border border-[var(--line)] hover:bg-[var(--bg-2)] hover:border-[var(--line-2)] shadow-[0_1px_0_rgba(255,255,255,.7)_inset,0_1px_2px_rgba(11,11,15,.04)]',
    danger:  'bg-white text-[var(--ink-3)] border border-[var(--line)] hover:bg-[rgba(193,48,48,.06)] hover:text-[#C13030] hover:border-[rgba(193,48,48,.25)]',
    primary: 'bg-[var(--ink)] text-[var(--bg)] border border-[var(--ink)] hover:bg-[var(--ink-2)] shadow-[0_6px_24px_rgba(11,11,15,.18)]',
  }
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={ariaLabel} className={`${base} ${styles[variant]}`}>
      {children}
    </button>
  )
}

export default function Quiosco() {
  const navigate = useNavigate()
  const [pin, setPin] = useState('')
  const [vista, setVista] = useState('pin') // 'pin' | 'preguntar' | 'eligePausa' | 'feedback'
  const [empleado, setEmpleado] = useState(null)
  const [tiposPausa, setTiposPausa] = useState([])
  const [feedback, setFeedback] = useState(null)
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const [showDesactivar, setShowDesactivar] = useState(false)

  const empresaId = localStorage.getItem('kiosk_empresa_id')
  const empresaNombre = localStorage.getItem('kiosk_empresa_nombre') || 'Quiosco'
  const centroId = localStorage.getItem('kiosk_centro_id') || null
  const centroNombre = localStorage.getItem('kiosk_centro_nombre') || null

  const feedbackTimer = useRef(null)
  const longPressTimer = useRef(null)

  useEffect(() => {
    if (!empresaId) navigate('/login', { replace: true })
  }, [empresaId, navigate])

  // Defensa: limpiar token residual sin pasar por AuthContext.logout
  // (eso navega a /login y entraría en bucle con KioskGuard).
  useEffect(() => {
    if (localStorage.getItem('token')) {
      localStorage.removeItem('token')
    }
  }, [])

  const limpiar = useCallback(() => {
    setPin('')
    setError(null)
    setEmpleado(null)
    setTiposPausa([])
    setFeedback(null)
    setVista('pin')
  }, [])

  const mostrarFeedback = useCallback((data) => {
    setFeedback(data)
    setVista('feedback')
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current)
    feedbackTimer.current = setTimeout(() => { limpiar() }, FEEDBACK_MS)
  }, [limpiar])

  useEffect(() => () => {
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current)
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
  }, [])

  const ficharConPin = useCallback(async (pinValor) => {
    setCargando(true); setError(null)
    try {
      const { data } = await axios.post('/api/quiosco/fichar', {
        empresa_id: empresaId, pin: pinValor, centro_id: centroId
      })
      if (data.accion === 'entrada_registrada') {
        mostrarFeedback({ tipo: 'entrada', empleado: data.empleado, hora: data.hora })
      } else if (data.accion === 'fin_pausa_registrado') {
        mostrarFeedback({
          tipo: 'fin_pausa', empleado: data.empleado, hora: data.hora,
          extra: data.duracion_minutos ? `Pausa de ${data.duracion_minutos} min` : null
        })
      } else if (data.accion === 'preguntar') {
        setEmpleado(data.empleado); setTiposPausa(data.tipos_pausa || [])
        setVista('preguntar'); setPin(pinValor)
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'Error al fichar. Inténtalo de nuevo.'
      setError(msg); setPin('')
      setTimeout(() => setError(null), 4000)
    } finally {
      setCargando(false)
    }
  }, [empresaId, centroId, mostrarFeedback])

  const enviarDecision = useCallback(async (decision, tipoCodigo) => {
    setCargando(true); setError(null)
    try {
      const { data } = await axios.post('/api/quiosco/decision', {
        empresa_id: empresaId, pin, decision,
        tipo_pausa_codigo: tipoCodigo, centro_id: centroId
      })
      if (data.accion === 'salida_registrada') {
        mostrarFeedback({ tipo: 'salida', empleado: data.empleado, hora: data.hora })
      } else if (data.accion === 'pausa_iniciada') {
        mostrarFeedback({
          tipo: 'pausa', empleado: data.empleado, hora: data.hora, extra: data.tipo_nombre
        })
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'Error al procesar la decisión.'
      setError(msg)
      setTimeout(() => limpiar(), 2500)
    } finally {
      setCargando(false)
    }
  }, [empresaId, pin, centroId, mostrarFeedback, limpiar])

  // ----- handlers teclado -----
  const onDigit = (d) => {
    if (vista !== 'pin' || cargando) return
    if (pin.length < 8) setPin((p) => p + d)
  }
  const onDelete = () => {
    if (vista !== 'pin' || cargando) return
    setPin((p) => p.slice(0, -1))
  }
  const onEnter = () => {
    if (vista !== 'pin' || cargando) return
    if (pin.length < 5) {
      setError('El PIN debe tener al menos 5 dígitos')
      setTimeout(() => setError(null), 3000)
      return
    }
    ficharConPin(pin)
  }

  // ----- long-press logo -----
  const onLogoPressStart = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
    longPressTimer.current = setTimeout(() => setShowDesactivar(true), LONG_PRESS_MS)
  }
  const onLogoPressEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null }
  }

  if (!empresaId) return null

  // Texto del feedback según tipo
  const feedbackLabel = (tipo) => ({
    entrada: 'Entrada registrada',
    salida: 'Salida registrada',
    pausa: 'Pausa iniciada',
    fin_pausa: 'Fin de pausa',
  })[tipo] || ''

  return (
    <div className="fixed inset-0 bg-[var(--bg)] flex flex-col text-[var(--ink)]">

      {/* Header */}
      <header className="flex items-center justify-between px-7 py-5 border-b border-[var(--line)]">
        <div
          className="flex items-center gap-3 cursor-pointer select-none"
          onMouseDown={onLogoPressStart}
          onMouseUp={onLogoPressEnd}
          onMouseLeave={onLogoPressEnd}
          onTouchStart={onLogoPressStart}
          onTouchEnd={onLogoPressEnd}
          title="Mantén pulsado 5 segundos para desactivar el modo quiosco"
        >
          <div className="w-9 h-9 rounded-[10px] ia-grad-bg flex items-center justify-center">
            <FiClock className="text-white w-5 h-5" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[16px] text-[var(--ink)]">{empresaNombre}</div>
            <div className="eyebrow mt-0.5">
              {centroNombre ? `§ ${centroNombre}` : 'Modo Quiosco'}
            </div>
          </div>
        </div>
        <Reloj />
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 overflow-y-auto">
        <div className="w-full max-w-[480px]">

          {vista === 'pin' && (
            <div className="space-y-8">
              <div className="text-center">
                <div className="eyebrow">§ 01 · Identifícate</div>
                <h1 className="font-display text-[36px] sm:text-[42px] leading-[1.05] mt-2.5">
                  Introduce <span className="font-display-i text-[var(--ink-3)]">tu PIN</span>
                </h1>
                <p className="text-[13px] text-[var(--ink-3)] mt-2 max-w-md mx-auto">
                  El sistema decidirá la acción (entrada, pausa o salida) según tu estado.
                </p>
              </div>

              <PinDots pin={pin} />

              {error && (
                <div className="ui-card p-3.5 text-center text-[13px] text-[#C13030] border-[rgba(193,48,48,.25)]">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-3 gap-2.5">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                  <KeyBtn key={d} onClick={() => onDigit(String(d))} disabled={cargando || pin.length >= 8} ariaLabel={`Dígito ${d}`}>
                    {d}
                  </KeyBtn>
                ))}
                <KeyBtn onClick={onDelete} disabled={cargando || pin.length === 0} variant="danger" ariaLabel="Borrar">
                  <FiDelete className="w-6 h-6" />
                </KeyBtn>
                <KeyBtn onClick={() => onDigit('0')} disabled={cargando || pin.length >= 8} ariaLabel="Dígito 0">
                  0
                </KeyBtn>
                <KeyBtn onClick={onEnter} disabled={cargando || pin.length < 5} variant="primary" ariaLabel="Confirmar">
                  {cargando
                    ? <span className="inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    : <FiCheck className="w-6 h-6" />}
                </KeyBtn>
              </div>
            </div>
          )}

          {vista === 'preguntar' && empleado && (
            <div className="space-y-6 text-center">
              <div className="eyebrow">§ 02 · Decide</div>
              <h1 className="font-display text-[36px] sm:text-[42px] leading-[1.05]">
                Hola, <span className="font-display-i text-[var(--ink-3)]">{empleado.nombre}</span>
              </h1>
              <p className="text-[14px] text-[var(--ink-3)]">¿Qué quieres hacer?</p>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setVista('eligePausa')}
                  disabled={cargando}
                  className="ui-card p-6 flex flex-col items-center justify-center gap-2.5 h-32 transition-all hover:-translate-y-px active:scale-[.98] disabled:opacity-50"
                >
                  <FiPause className="w-7 h-7 text-[var(--ink)]" />
                  <span className="font-display text-[18px]">Pausa</span>
                </button>
                <button
                  type="button"
                  onClick={() => enviarDecision('salida')}
                  disabled={cargando}
                  className="bg-[var(--ink)] text-[var(--bg)] rounded-[16px] p-6 flex flex-col items-center justify-center gap-2.5 h-32 transition-all hover:-translate-y-px active:scale-[.98] disabled:opacity-50 shadow-[0_6px_24px_rgba(11,11,15,.18)]"
                >
                  <FiLogOut className="w-7 h-7" />
                  <span className="font-display text-[18px]">Salida</span>
                </button>
              </div>

              <button
                type="button"
                onClick={limpiar}
                className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ink-3)] hover:text-[var(--ink)] mt-2 transition-colors"
              >
                <FiArrowLeft className="w-4 h-4" />
                Cancelar
              </button>

              {error && (
                <div className="ui-card p-3.5 text-[13px] text-[#C13030] border-[rgba(193,48,48,.25)]">
                  {error}
                </div>
              )}
            </div>
          )}

          {vista === 'eligePausa' && empleado && (
            <div className="space-y-6 text-center">
              <div className="eyebrow">§ 03 · Tipo de pausa</div>
              <h1 className="font-display text-[28px] sm:text-[34px] leading-[1.1]">
                <span className="font-display-i text-[var(--ink-3)]">{empleado.nombre}</span>, elige tipo
              </h1>

              {tiposPausa.length === 0 ? (
                <p className="text-[13px] text-[var(--ink-3)]">No hay tipos de pausa configurados para esta empresa.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-2">
                  {tiposPausa.map((tp) => {
                    const Icon = ICON_MAP[tp.icono] || FiPause
                    return (
                      <button
                        key={tp.codigo}
                        type="button"
                        onClick={() => enviarDecision('pausa', tp.codigo)}
                        disabled={cargando}
                        className="ui-card p-4 flex flex-col items-center justify-center gap-2 h-24 transition-all hover:-translate-y-px hover:border-[var(--ink-3)] active:scale-[.98] disabled:opacity-50"
                      >
                        <Icon className="w-6 h-6 text-[var(--ink)]" />
                        <span className="font-display text-[13.5px] text-[var(--ink-2)]">{tp.nombre}</span>
                      </button>
                    )
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={() => setVista('preguntar')}
                className="inline-flex items-center gap-1.5 text-[13px] text-[var(--ink-3)] hover:text-[var(--ink)] mt-2 transition-colors"
              >
                <FiArrowLeft className="w-4 h-4" />
                Atrás
              </button>

              {error && (
                <div className="ui-card p-3.5 text-[13px] text-[#C13030] border-[rgba(193,48,48,.25)]">
                  {error}
                </div>
              )}
            </div>
          )}

          {vista === 'feedback' && feedback && (
            <div className="text-center space-y-5">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--ink)] text-[var(--bg)]">
                <FiCheck className="w-10 h-10" />
              </div>
              <div className="eyebrow">{feedbackLabel(feedback.tipo)}</div>
              <h1 className="font-display text-[34px] sm:text-[40px] leading-[1.05]">
                {feedback.empleado?.nombre}
              </h1>
              {feedback.extra && (
                <p className="text-[14px] text-[var(--ink-3)]">{feedback.extra}</p>
              )}
              <p className="font-display text-[28px] tnum text-[var(--ink-2)]">{feedback.hora}</p>
            </div>
          )}

        </div>
      </main>

      {showDesactivar && (
        <DesactivarQuioscoModal onClose={() => setShowDesactivar(false)} />
      )}
    </div>
  )
}
